import { readonly, reactive } from "vue";
import { useEnvVars } from "./useEnvVars";

const config = reactive({
  /**
   * UID of the current user.
   *
   * @type {number}
   */
  currentUser: 0,

  /**
   * String representing the display name for the current user.
   *
   * @tupe {String}
   */
  currentUsername: "",

  /**
   * String representing the jsonapi base url.
   *
   * @type {String}
   */
  baseUrl: "",

  /**
   * String representing the BioRAFT3 base url.
   *
   * @type {String}
   */
  b3baseUrl: "",

  /**
   * String representing the base url for all REST requests.
   *
   * @type {String}
   */
  restUrl: "",

  /**
   * String representing the BioRAFT4 base url that is NEVER PROXIED.
   *
   * @type {String}
   */
  entryUrl: "",

  /**
   * JWT token for authentication.
   *
   * @type {String}
   */
  jwt: "",

  /**
   * JWT token for direct file access.
   *
   * @type {String}
   */
  fileJwt: "",

  /**
   * Anti-CSRF token for B4.
   *
   * @type {String}
   */
  csrfToken: "",

  /**
   * Flag to determine whether or not the running context is BioRAFT4.
   *
   * @type {boolean}
   */
  isB4: false,

  /**
   * Array of users Chemtracker permissions.
   *
   * @type {array}
   */
  userChemPerms: [],

  /**
   * Flag to determine whether or not Facilities are enabled.
   *
   * @type {boolean}
   */
  useFacilities: false,

  /**
   * Nid of context group/space/building.
   *
   * @type {string}
   */
  groupContext: "",

  /**
   * UUID of context group/space/building.
   *
   * @type {string}
   */
  groupContextUuid: "",

  /**
   * String representing the group entity bundle.
   *
   * @type {string}
   */
  groupType: "",

  /**
   * String representing the current node bundle.
   *
   * @type {string}
   */
  nodeType: "",

  /**
   * String representing the group entity title.
   *
   * @type {string}
   */
  groupTitle: "",

  /**
   * Flag to determine if ChemTracker is enabled.
   *
   * @type {boolean}
   */
  chemTrackerEnabled: false,

  /**
   * Flag to determine if Incident Management is enabled.
   *
   * @type {boolean}
   */
  incidentManagementEnabled: false,

  /**
   * Flag to determine if Control Areas are enabled.
   *
   * @type {boolean}
   */
  controlAreasEnabled: false,

  /**
   * Flag to determine if Floors are enabled.
   *
   * @type {boolean}
   */
  floorsEnabled: false,

  /**
   * Flag to determine if Campuses are enabled.
   *
   * @type {boolean}
   */
  campusesEnabled: false,

  /**
   * Flag to determine if the Door Signs module is enabled.
   *
   * @type {boolean}
   */
  doorSignsEnabled: false,

  /**
   * Flag to determine if Animal Contact is enabled in medical surveillance.
   *
   * @type {boolean}
   */
  animalContactEnabled: false,

  /**
   * Flag to determine if spaces are using Max BSL Allowed fields.
   *
   * @type {boolean}
   */
  maxBslEnabled: false,

  /**
   * Array of options for Max BSL Allowed fields.
   *
   * @type {array}
   */
  maxBslOptions: [],

  /**
   * Currently configured bio_regulations.
   *
   * @type {string}
   */
  bioRegulations: "usa",
  b4BaseUrl: "",

  chemTrackerConfig: {},
  chemicalSearchHost: "",
  chemicalSearchIndexes: {},
  chemicalSearchAuthToken: "",
  physicalStates: {},

  wasteCodesEnabled: false,
  timezone: "UTC",
  customerName: "",
  customerShortName: "",
  isDromoContext: false,
});

function updateModule(module) {
  config.module = module;
}

function updateChemicalSearchToken(token) {
  if (config.isB4) {
    window.drupalSettings.chemical_search.token = token;
  } else {
    window.Drupal.settings.chemical_search.token = token;
  }
  config.chemicalSearchAuthToken = token;
}

function parseB3(Drupal) {
  if (typeof Drupal !== "undefined") {
    // Supports BioRAFT3.
    if (Drupal?.settings) {
      if (Array.isArray(Drupal.settings?.user?.current)) {
        config.currentUser = Drupal.settings?.user?.current?.[0];
      } else {
        config.currentUser = Drupal.settings?.user?.current;
      }
      if (Drupal.settings.timezone) {
        config.timezone = Drupal.settings.timezone;
      }
      if (Drupal.settings.hazwaste) {
        config.wasteCodesEnabled = Drupal.settings.hazwaste.waste_codes_enabled;
      }
      if (Drupal.settings.chemical_search) {
        config.chemicalSearchHost = Drupal.settings.chemical_search.endpoint;
        config.chemicalSearchAuthToken = Drupal.settings.chemical_search.token;
        config.chemicalSearchIndexes = Drupal.settings.chemical_search.index;
        config.physicalStates = Drupal.settings.chemical_search.physical_states;
      }
      if (Drupal.settings?.chemtracker) {
        config.chemTrackerConfig = Drupal.settings?.chemtracker;
        config.chemTrackerEnabled = true;
        config.jwt = Drupal.settings?.chemtracker?.token;
        config.baseUrl = Drupal.settings?.chemtracker?.webservices?.endpoint;
        config.restUrl = `${
          Drupal.settings?.chemtracker?.webservices?.endpoint || ""
        }/raft/rest`;
        config.userChemPerms = Drupal.settings?.chemtracker?.perms;
      }
      config.bioTrackingSpecifyUsage =
        Drupal.settings?.bioraft?.biologicals?.specify_usage !== false;
      if (Drupal.settings?.bioraft) {
        config.jwt = config.jwt || Drupal.settings?.bioraft?.token;
        config.baseUrl =
          config.baseUrl || Drupal.settings?.bioraft?.b4_base_url;
        config.restUrl =
          config.restUrl ||
          `${Drupal.settings?.bioraft?.b4_base_url || ""}/raft/rest`;
        config.fileJwt = Drupal.settings?.bioraft?.file_jwt;

        config.customerShortName =
          Drupal?.settings?.bioraft?.customer_context_short_name || "";
        config.customerName =
          Drupal?.settings?.bioraft?.customer_context_legal_name || "";

        if (Drupal.settings?.bioraft?.incident_management?.enabled) {
          config.incidentManagementEnabled = true;
        }
        if (Drupal.settings?.bioraft?.door_signs?.enabled) {
          config.doorSignsEnabled = true;
        }
        if (Drupal.settings?.bioraft?.control_area?.enabled) {
          config.controlAreasEnabled = true;
        }
        if (Drupal.settings?.bioraft?.floors?.enabled) {
          config.floorsEnabled = true;
        }
        if (Drupal.settings?.bioraft?.campus?.enabled) {
          config.campusesEnabled = true;
        }
        if (Drupal.settings?.bioraft?.facilities) {
          config.useFacilities = true;
        }
        if (Drupal.settings.bioraft?.biologicals?.bio_regulations) {
          config.bioRegulations =
            Drupal.settings.bioraft.biologicals.bio_regulations;
        }
        if (Drupal.settings.bioraft?.raft_space?.max_bsl?.enabled) {
          config.maxBslEnabled = true;
          config.maxBslOptions =
            Drupal.settings.bioraft?.raft_space?.max_bsl?.options;
        }
        if (
          Drupal.settings?.bioraft?.medical_surveillance?.animal_contact_enabled
        ) {
          config.animalContactEnabled = true;
        }
        config.groupContext = Drupal.settings?.bioraft?.group_context;
        config.groupContextUuid = Drupal.settings?.bioraft?.group_context_uuid;
        config.groupType = Drupal.settings?.bioraft?.group_type_machine_name;
        config.nodeType = Drupal.settings?.bioraft?.node_context?.node_type;
        config.groupTitle = Drupal.settings?.bioraft?.group_title;
      }
    }
  }
  return config;
}

function parseB4(drupalSettings) {
  // BioRAFT 4.
  if (typeof drupalSettings !== "undefined" && drupalSettings?.user) {
    config.currentUser = drupalSettings.user.uid;
    config.currentUsername = drupalSettings.user.display_name;
    config.isB4 = true;
    config.useFacilities = true;
    config.baseUrl = window.location.origin;
    config.restUrl = config.baseUrl + "/raft/rest";
    config.b3baseUrl = config.baseUrl.replace("-b4", "");
    if (drupalSettings?.raft?.customer_context) {
      config.customerShortName =
        drupalSettings.raft.customer_context.short_name;
      config.customerName = drupalSettings.raft.customer_context.legal_name;
    }
    if (drupalSettings.hazwaste) {
      config.wasteCodesEnabled = drupalSettings.hazwaste.waste_codes_enabled;
    }
    if (drupalSettings.chemical_search) {
      config.chemicalSearchHost = drupalSettings.chemical_search.endpoint;
      config.chemicalSearchAuthToken = drupalSettings.chemical_search.token;
      config.chemicalSearchIndexes = drupalSettings.chemical_search.index;
      config.physicalStates = drupalSettings.chemical_search.physical_states;
    }
    if (drupalSettings.chemtracker) {
      config.chemTrackerEnabled = true;
      if (drupalSettings.chemtracker.perms) {
        config.userChemPerms = drupalSettings.chemtracker.perms;
      }
      if (drupalSettings.chemtracker.csrf_token) {
        // @todo - populate this in B3 also by making a request to B4 /rest/session/token.
        config.csrfToken = drupalSettings.chemtracker.csrf_token;
      }
      if (drupalSettings.chemtracker.context_id) {
        config.groupContext = drupalSettings.chemtracker.context_id;
      }
    }
    config.bioTrackingSpecifyUsage =
      drupalSettings?.raft?.biologicals?.specify_usage !== false;
    if (drupalSettings?.raft?.csrf_token) {
      config.csrfToken = drupalSettings.raft.csrf_token;
    }
    if (drupalSettings.raft?.hasOwnProperty("csrf_token")) {
      // @todo - populate this in B3 also by making a request to B4 /rest/session/token.
      config.csrfToken = drupalSettings.raft?.csrf_token;
    }
    if (drupalSettings.raft?.hasOwnProperty("timezone")) {
      config.timezone = drupalSettings.raft?.timezone;
    }
    if (drupalSettings.raft?.hasOwnProperty("group_context")) {
      config.groupContext = drupalSettings.raft.group_context.context_id;
      config.groupContextUuid = drupalSettings.raft.group_context.context_uuid;
      config.groupTitle = drupalSettings.raft.group_context.context_title;
      config.groupType = drupalSettings.raft.group_context.context_type;
    }
    if (drupalSettings?.raft?.hasOwnProperty("node_context")) {
      config.nodeType = drupalSettings.raft.node_context.bundle;
    }
    if (drupalSettings.raft?.incident_management?.enabled) {
      config.incidentManagementEnabled = true;
    }
    if (drupalSettings.raft?.door_signs?.enabled) {
      config.doorSignsEnabled = true;
    }
    if (drupalSettings.raft?.control_area?.enabled) {
      config.controlAreasEnabled = true;
    }
    if (drupalSettings.raft?.floors?.enabled) {
      config.floorsEnabled = true;
    }
    if (drupalSettings.raft?.campus?.enabled) {
      config.campusesEnabled = true;
    }
    if (drupalSettings.raft.biologicals?.bio_regulations) {
      config.bioRegulations = drupalSettings.raft.biologicals.bio_regulations;
    }
    if (drupalSettings.raft?.raft_space?.max_bsl?.enabled) {
      config.maxBslEnabled = true;
      config.maxBslOptions = drupalSettings.raft?.raft_space?.max_bsl?.options;
    }
  }
  config.groupType =
    config.groupType || (drupalSettings?.chemtracker?.context_type ?? "");
  return config;
}

function fallbackForUrls() {
  const { origin, protocol, hostname, port } = window.location;
  const { devServerRunning, browseThruDevServer, isCypress } = useEnvVars();
  const hostnameWithB4 = hostname.includes("-b4")
    ? hostname
    : hostname.replace(hostname.split(".")[0], hostname.split(".")[0] + "-b4");

  config.entryUrl = `${config.baseUrl}/buildjs/`;

  if (isCypress) {
    config.baseUrl = `${protocol}//${hostname}:${port}`;
    config.entryUrl = `${protocol}//${hostname}:${port}/buildjs/`;
    config.restUrl = `${protocol}//${hostname}:${port}/raft/rest`;
    config.b3baseUrl = config.baseUrl.replace("-b4", "");
  } else if (browseThruDevServer) {
    config.entryUrl = "http://localhost:8080/buildjs/";
    config.b3baseUrl = "http://localhost:8080/proxy/b3";
    config.baseUrl = "http://localhost:8080/proxy/b4";
    config.restUrl = "http://localhost:8080/proxy/b4/raft/rest";
  } else {
    if (config.b3baseUrl === "") {
      config.b3baseUrl = origin;
    }
    if (config.baseUrl === "") {
      config.baseUrl = hostnameWithB4;
      config.entryUrl = `${config.baseUrl}/buildjs/`;
    }
    if (config.restUrl === "") {
      config.restUrl = `${hostnameWithB4}/raft/rest`;
    }
  }

  if (devServerRunning) {
    config.entryUrl = "http://localhost:8080/buildjs/";
  }
}

// eslint-disable-next-line import/prefer-default-export
export function useDrupalSettings() {
  const { isB3, isB4, drupalSettings, Drupal } = useEnvVars();

  if (isB3) {
    parseB3(Drupal);
  } else if (isB4) {
    parseB4(drupalSettings);
  } else {
    try {
      const el = document.querySelector(
        "[data-drupal-selector=drupal-settings-json]",
      );
      const unparsed = el?.textContent;
      if (unparsed) {
        const altSettings = JSON.parse(unparsed);
        parseB4(altSettings);
      }
    } catch (e) {
      // eslint-disable-next-line
      console.error(e);
    }
  }
  config.isDromoContext =
    config.groupType !== "building" && config.groupType !== "space";
  fallbackForUrls();

  return {
    config: readonly(config),
    updateModule,
    updateChemicalSearchToken,
    parseB3,
    parseB4,
  };
}
